import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { siteConfig } from '../../mocks/siteData';

interface HeroProps {
  headline: string;
  subheadline: string;
  primaryCTA: { label: string; href: string };
  secondaryCTA?: { label: string; href: string };
  backgroundImage: string;
  badge?: string;
  eyebrow?: string;
}

export default function PageHero({
  headline,
  subheadline,
  primaryCTA,
  secondaryCTA,
  backgroundImage,
  badge,
  eyebrow,
}: HeroProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    const handleScroll = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          setOffset(window.scrollY * 0.3);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Parallax Background */}
      <div
        className="absolute inset-0 w-full h-[120%] -top-[10%] bg-center bg-cover"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          transform: `translateY(${offset}px)`,
          willChange: 'transform',
        }}
      />

      {/* Layered Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/30" />
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#0A0A0A] to-transparent" />

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center px-6 lg:px-16 xl:px-24 pt-32 pb-24">
        <div className={`max-w-3xl transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {eyebrow && (
            <div className="inline-flex items-center gap-2 mb-6">
              <span className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-sans font-semibold tracking-[0.3em] uppercase">{eyebrow}</span>
            </div>
          )}
          {badge && (
            <div className="inline-block border border-gold/40 bg-gold/10 text-gold text-xs font-sans font-medium px-4 py-1.5 tracking-widest uppercase mb-6">
              {badge}
            </div>
          )}
          <h1 className="font-serif text-white text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] mb-8">
            {headline}
          </h1>
          <p className="text-white/60 text-lg font-sans leading-relaxed mb-10 max-w-xl">
            {subheadline}
          </p>
          <div className="flex flex-wrap gap-4 items-center">
            <Link
              to={primaryCTA.href}
              className="bg-gold hover:bg-gold-light text-[#0A0A0A] font-sans font-semibold text-sm px-8 py-4 uppercase tracking-widest transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {primaryCTA.label}
            </Link>
            {secondaryCTA ? (
              <Link
                to={secondaryCTA.href}
                className="border border-white/30 hover:border-white/60 text-white/70 hover:text-white font-sans font-medium text-sm px-8 py-4 uppercase tracking-widest transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                {secondaryCTA.label}
              </Link>
            ) : (
              <a
                href={siteConfig.phoneHref}
                className="flex items-center gap-3 text-white/60 hover:text-gold font-sans text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-10 h-10 flex items-center justify-center border border-white/20 hover:border-gold/40 transition-colors">
                  <i className="ri-phone-fill text-base" />
                </div>
                <div>
                  <div className="text-[10px] text-white/30 tracking-widest uppercase font-sans mb-0.5">Call Now</div>
                  <div>{siteConfig.phone}</div>
                </div>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="relative z-10 flex flex-col items-center pb-10 gap-1">
        <span className="text-white/25 text-[9px] tracking-[0.35em] uppercase font-sans">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-white/30 to-transparent" />
      </div>
    </section>
  );
}
